import { FacilityFormValues } from '~/features/types';
import { capitalizeEveryWord, stripLocationSuffix } from '~/features/utils';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import { EmployerFacilityValues } from './types';

// [MCV-1876] Lives in ~/features/utils so the vault facility code can share it.
export { stripLocationSuffix };

const optionFromValue = (value: string | null | undefined) => {
  const optionValue = value ?? '';
  return {
    label: optionValue,
    value: optionValue,
  };
};

export const formatEmployerFacilityLocation = (facility: EmployerFacility) => {
  const address = facility.facility?.address;
  return [address?.city, address?.stateCode].filter(Boolean).join(', ');
};

// [MCV-1876] The picker's option label is "Name (CITY, ST)" so same-named
// facilities can be told apart. That suffix is picker chrome, not part of the
// name: saving it stored "Name (CITY, ST)" in employer_facilities.facility_name,
// and every screen that appends the location then showed it twice. The stored
// name is the facility's own name, cased the way the label casts it. A catalog
// row whose own name already ends in its location (a user-added facility named
// from an old label) is stripped too, or the suffix would be stored again.
export const toStoredFacilityName = (
  facility: Pick<FacilityFormValues, 'label' | 'rawName'> & {
    address?: { city?: string | null; stateCode?: { value?: string | null } | null } | null;
  }
): string => {
  const name = facility.rawName ? capitalizeEveryWord(facility.rawName) : facility.label;
  return stripLocationSuffix(name, facility.address?.city, facility.address?.stateCode?.value);
};

export const getEmployerFacilityDisplayName = (facility: EmployerFacility) => {
  const address = facility.facility?.address;
  const storedName = facility.facilityName
    ? stripLocationSuffix(facility.facilityName, address?.city, address?.stateCode)
    : '';
  return storedName || facility.facility?.name || 'Facility';
};

export const employerFacilityToForm = (facility: EmployerFacility): EmployerFacilityValues => {
  const displayName = getEmployerFacilityDisplayName(facility);
  return {
    id: facility.id,
    facilityName: {
      label: displayName,
      value: displayName,
    },
    facilityId: facility.facilityId.toString(),
    facilityNpi: facility.facilityNpi || '',
    clinicalSettingCategory: optionFromValue(facility.clinicalSettingCategory),
    clinicalSetting: optionFromValue(facility.clinicalSetting),
    facilitySize: optionFromValue(facility.facilitySize),
    areaType: optionFromValue(facility.areaType),
    isPrimary: facility.isPrimary,
  };
};
